/**
 * RISKS-VALIDATED.TSX
 * List of risks grouped by family with card layout
 */
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useNavigate } from "react-router-dom"
import Card from "@/components/card"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import RiskHelpModal from "@/features/risks/entities/risk/ui/modals/RisksHelpModal"
import RiskDetailModal from "@/features/risks/entities/risk/ui/modals/RiskDetailModal"
import Chip from "@/components/chip"
import {
  faChevronDown,
  faChevronUp,
  faPen,
  faEye,
  faCheckCircle,
  faCircleXmark,
  faLayerGroup,
  faClipboardList,
  faHeartPulse,
  faHelmetSafety
} from "@fortawesome/free-solid-svg-icons"
import { Risk } from "@/features/risks/entities/risk/model/risk.types"
import Highlighter from "react-highlight-words"
import PagePlaceholder from "@/components/page-placeholder"
import { isToday } from "@/utils/format-date.utils"
import Tooltip from "@/components/tooltip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ListButton from "@/components/list-button"
import UnitSizeTooltip from "../../unit/ui/UnitSizeTooltip"
import getColor from "@/utils/get-color.utils"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import UnitBadge from "@/features/risks/entities/risk/ui/UnitBadge"
import { t } from "i18next"
import { useRisksContext } from "../context/RisksContext"
import unitStore from "../../unit/model/unit.store"

type ModalName = "MODAL_HELP" | "MODAL_VIEW_RISK"

interface Props {
  disableAutoOpen?: boolean
  backTo?: string
}

export default function RisksValidated({ disableAutoOpen = false, backTo = "/risks" }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const search = useAppSelector(riskStore.selectors.search)
  const riskFamilies = useAppSelector(riskStore.selectors.listSelectedByUnits)
  const selectedDate = useAppSelector(riskStore.selectors.selectedDate)
  const selectedUnits = useAppSelector(unitStore.selectors.selected)
  const optionalRiskAttributes = useAppSelector(organizationStore.selectors.active)?.optionalRiskAttributes ?? []

  const [modal, setModal] = useState<null | ModalName>(null)
  const [viewingRisk, setViewingRisk] = useState<Risk | null>(null)
  const { openFamilies, setOpenFamilies, toggleFamily, saveScrollPosition, restoreScrollPosition } = useRisksContext()

  // Check if date filter is active
  const isDateFiltered = selectedDate && !isToday(selectedDate)

  // Créer une liste de tous les risques en ordre d'affichage
  const allRisks = riskFamilies.flatMap((family) => family.Risks)

  // Auto-open families when unit filter changes
  useEffect(() => {
    if (disableAutoOpen) return

    if (selectedUnits && (Array.isArray(selectedUnits) ? selectedUnits.length > 0 : selectedUnits)) {
      // Get families that belong to the selected unit(s)
      const familiesToOpen = riskFamilies
        .filter((family) => {
          const familyUnitIds = new Set([
            ...family.Risks.map((risk: any) => risk.UnitId),
            ...family.Risks.flatMap((risk: any) => risk.associatedRisks?.map((ar: any) => ar.UnitId) || [])
          ])
          return Array.isArray(selectedUnits)
            ? selectedUnits.some((unitId: string) => familyUnitIds.has(unitId))
            : familyUnitIds.has(selectedUnits)
        })
        .map((family) => family.id)

      if (familiesToOpen.length > 0) {
        setOpenFamilies(familiesToOpen)
      }
    }
  }, [selectedUnits, riskFamilies, setOpenFamilies, disableAutoOpen])

  // Reset date filter when returning to this page
  useEffect(() => {
    dispatch(riskStore.actions.setSelectedDate(new Date()))
  }, [dispatch])

  // Restore scroll position on mount
  useEffect(() => {
    restoreScrollPosition()
  }, [])

  function clickAction(risk: Risk) {
    saveScrollPosition()
    dispatch(riskStore.actions.setActive(risk))
    navigate(`/risks/risk/${risk.id}`, { state: { backTo } })
  }

  function closeModal() {
    setModal(null)
    setViewingRisk(null)
  }

  function openViewRisk(risk: Risk) {
    dispatch(riskStore.actions.setActive(risk))
    setViewingRisk(risk)
    setModal("MODAL_VIEW_RISK")
  }

  function goToNextRisk() {
    const currentIndex = allRisks.findIndex((r) => r.id === viewingRisk?.id)
    const nextIndex = (currentIndex + 1) % allRisks.length
    openViewRisk(allRisks[nextIndex])
  }

  function goToPreviousRisk() {
    const currentIndex = allRisks.findIndex((r) => r.id === viewingRisk?.id)
    const prevIndex = (currentIndex - 1 + allRisks.length) % allRisks.length
    openViewRisk(allRisks[prevIndex])
  }

  function getEmptyLabel() {
    if (search.value) return "Aucun résultat ne correspond à votre recherche"
    if (!isToday(selectedDate)) return "Nous n'avons pas trouvé de risque enregistré à cette période"
    return "Aucun risque n'a encore été déclaré dans votre Document Unique"
  }

  function openHelp() {
    setModal("MODAL_HELP")
  }

  function isRiskIncomplete(risk: Risk): boolean {
    return Object.entries(risk.attributes).some(([key, value]: any) => {
      if (optionalRiskAttributes.includes(key)) return false
      return !value || value.trim?.() === ""
    })
  }

  return (
    <div className="h-full mt-3">
      {modal === "MODAL_HELP" && <RiskHelpModal onClose={closeModal} item="rr" />}

      {modal === "MODAL_VIEW_RISK" && (
        <RiskDetailModal
          risk={viewingRisk}
          allRisks={allRisks}
          onClose={closeModal}
          onNavigateNext={goToNextRisk}
          onNavigatePrevious={goToPreviousRisk}
          onEdit={() => clickAction(viewingRisk!)}
          isIncomplete={viewingRisk ? isRiskIncomplete(viewingRisk) : false}
          isDateFiltered={isDateFiltered}
        />
      )}

      {riskFamilies.length === 0 && <PagePlaceholder title={getEmptyLabel()} image="coming-soon" />}

      <div className="flex flex-col justify-between h-full">
        {riskFamilies.map((riskFamily) => {
          const isOpen = openFamilies.includes(riskFamily.id)

          if (!riskFamily.Risks.length) return null

          return (
            <div key={riskFamily.id} id={`family-${riskFamily.id}`}>
              {/* Family Header */}
              <div style={{ backgroundColor: "#f5f5f5", marginTop: "0.5rem" }} className="rounded-lg">
                <Card
                  className="bg-white px-6 py-3 rounded-lg border border-gray-300 shadow-sm"
                  isWithoutPadding
                  isNotCardClass>
                  <div
                    className="flex justify-between items-center cursor-pointer gap-4 _hover"
                    onClick={() => toggleFamily(riskFamily.id)}>
                    <div className="flex flex-col gap-0.5 -mt-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faLayerGroup} className="grey-t flex-shrink-0" />
                        <h3 className="text-base font-semibold truncate max-w-sm">
                          <Highlighter
                            searchWords={search.value ? [search.value] : []}
                            autoEscape={true}
                            textToHighlight={riskFamily.name}
                          />
                        </h3>
                      </div>
                      <span className="text-sm text-gray-400 -mt-4">
                        {(() => {
                          const maxDate = riskFamily.Risks.reduce((max, current) => {
                            if (!current.dateValidated) return max
                            return !max || new Date(current.dateValidated) > new Date(max) ? current.dateValidated : max
                          }, null as any)
                          return maxDate
                            ? `Dernière mise à jour ${new Date(maxDate).toLocaleDateString("fr-FR")}`
                            : "Aucune mise à jour"
                        })()}
                      </span>
                    </div>

                    <div className="flex-1" />

                    <div
                      style={{ gridTemplateColumns: "80px 80px 40px 120px 40px" }}
                      className="grid gap-4 items-center flex-shrink-0">
                      <div className="flex justify-center">
                        <UnitSizeTooltip size={riskFamily.size} />
                      </div>
                      <div className="flex justify-center gap-3 items-center">
                        {(() => {
                          const phyCount = riskFamily.Risks.filter((r: any) => !r.rps).length
                          const rpsCount = riskFamily.Risks.filter((r: any) => r.rps === true).length
                          return (
                            <>
                              {phyCount > 0 && (
                                <Tooltip text={`Risques Physiques - ${phyCount}`} position="top">
                                  <div className="relative flex items-center justify-center">
                                    <div className="w-6 h-6 flex items-center justify-center rounded-[50%] grey">
                                      <FontAwesomeIcon icon={faHelmetSafety} style={{ fontSize: "12px", color: "white" }} />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full grey text-white flex items-center justify-center text-sm font-bold">
                                      {phyCount}
                                    </div>
                                  </div>
                                </Tooltip>
                              )}
                              {rpsCount > 0 && (
                                <Tooltip text={`Risques Psychosociaux - ${rpsCount}`} position="top">
                                  <div className="relative flex items-center justify-center">
                                    <div className="w-6 h-6 flex items-center justify-center rounded-[50%] grey">
                                      <FontAwesomeIcon icon={faHeartPulse} style={{ fontSize: "12px", color: "white" }} />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full grey text-white flex items-center justify-center text-sm font-bold">
                                      {rpsCount}
                                    </div>
                                  </div>
                                </Tooltip>
                              )}
                            </>
                          )
                        })()}
                      </div>
                      <div className="flex justify-center">
                        {(() => {
                          const incompleteCount = riskFamily.Risks.filter(isRiskIncomplete).length
                          return incompleteCount > 0 ? (
                            <Tooltip
                              text={`${incompleteCount} fiche${incompleteCount > 1 ? "s" : ""} à compléter`}
                              position="top">
                              <div className="relative flex items-center justify-center">
                                <div
                                  className="w-6 h-6 flex items-center justify-center rounded-[50%]"
                                  style={{ backgroundColor: getColor("red") }}>
                                  <FontAwesomeIcon
                                    icon={faClipboardList}
                                    style={{
                                      fontSize: "12px",
                                      color: "white"
                                    }}
                                  />
                                </div>
                                <div
                                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-white flex items-center justify-center text-sm font-bold"
                                  style={{ backgroundColor: getColor("red") }}>
                                  {incompleteCount}
                                </div>
                              </div>
                            </Tooltip>
                          ) : null
                        })()}
                      </div>
                      <div className="flex justify-center">
                        <Tooltip text="Risque résiduel le plus élevé" position="left">
                          <Chip color={riskFamily.riskMax.valueEffectiveColor} onClick={openHelp}>
                            {riskFamily.riskMax.valueEffectiveLabel}
                          </Chip>
                        </Tooltip>
                      </div>
                      <div className="flex justify-center">
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                  {/* Risk Cards */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="grid grid-cols-3 gap-3 mt-6"
                        style={{ gridTemplateRows: "auto auto auto" }}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{
                          duration: 0.3,
                          ease: [0.34, 1.56, 0.64, 1]
                        }}>
                        {riskFamily.Risks.map((risk) => (
                          <div
                            key={risk.id}
                            className="row-span-3 max-w-[21rem] pt-6 px-5 py-7 rounded-lg border border-gray-400 shadow-md"
                            style={{ border: "solid 1px #9ca3af", display: "grid", gridTemplateRows: "subgrid", gap: "0.75rem" }}>
                              <div className="flex justify-between items-start gap-2">
                                <div className="flex-1 flex flex-col">
                                  <div className="flex gap-2 items-center">
                                    <FontAwesomeIcon
                                      icon={risk.rps ? faHeartPulse : faHelmetSafety}
                                      className="grey-t flex-shrink-0"
                                      style={{ fontSize: "14px" }}
                                    />
                                    <h4 className="font-semibold text-sm line-clamp-2 m-0">{risk.nameSecondary}</h4>
                                  </div>
                                  <span className="text-sm text-gray-400">
                                    {risk.dateValidated
                                      ? `Dernière mise à jour ${new Date(risk.dateValidated).toLocaleDateString("fr-FR")}`
                                      : "Dernière mise à jour -"}
                                  </span>
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-2">
                                  <Tooltip text="Visualiser" position="left">
                                    <ListButton onClick={() => openViewRisk(risk)} icon={faEye} />
                                  </Tooltip>
                                  <Tooltip
                                    text={isDateFiltered ? "Édition désactivée (consultation historique)" : "Éditer"}
                                    position="left">
                                    <div
                                      style={{
                                        opacity: isDateFiltered ? 0.4 : 1,
                                        pointerEvents: isDateFiltered ? "none" : "auto"
                                      }}>
                                      <ListButton onClick={() => clickAction(risk)} icon={faPen} />
                                    </div>
                                  </Tooltip>
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 self-start space-y-4">
                                <div>
                                  <div className="font-semibold mb-1.5">{t("Unités de travail concernées")}</div>
                                  <div className="flex flex-wrap gap-2">
                                    {risk.unitName && (
                                      <Tooltip
                                        text={
                                          isDateFiltered
                                            ? "Édition désactivée (consultation historique)"
                                            : `Modifier ce risque dans l'unité de travail : ${risk.unitName}`
                                        }
                                        position="top"
                                        width={200}>
                                        <UnitBadge
                                          unitName={risk.unitName}
                                          unitSize={risk.unitSize}
                                          isCurrentUnit={true}
                                        />
                                      </Tooltip>
                                    )}
                                    {risk.associatedRisks?.map((associatedRisk) =>
                                      associatedRisk.unitName ? (
                                        <Tooltip
                                          key={associatedRisk.id}
                                          text={
                                            isDateFiltered
                                              ? "Édition désactivée (consultation historique)"
                                              : `Modifier ce risque dans l'unité de travail : ${associatedRisk.unitName}`
                                          }
                                          position="top"
                                          width={200}>
                                          <div
                                            onClick={() => {
                                              if (isDateFiltered) return
                                              const siblings = [
                                                { ...risk, associatedRisks: [] },
                                                ...risk.associatedRisks.filter((ar) => ar.id !== associatedRisk.id)
                                              ]
                                              clickAction({ ...associatedRisk, associatedRisks: siblings })
                                            }}
                                            style={{ pointerEvents: isDateFiltered ? "none" : "auto" }}>
                                            <UnitBadge
                                              unitName={associatedRisk.unitName}
                                              unitSize={associatedRisk.unitSize}
                                              isCurrentUnit={false}
                                            />
                                          </div>
                                        </Tooltip>
                                      ) : null
                                    )}
                                  </div>
                                </div>
                                <div className="border-t border-gray-200 flex items-center gap-1">
                                  {(() => {
                                    const hasNoPrevention =
                                      (risk as any).prevention === 1 || (risk as any).prevention === 0
                                    const color = hasNoPrevention ? getColor("red") : getColor("green")
                                    return (
                                      <>
                                        <FontAwesomeIcon
                                          icon={hasNoPrevention ? faCircleXmark : faCheckCircle}
                                          size="xs"
                                          style={{ color }}
                                        />
                                        <span style={{ color }}>
                                          {hasNoPrevention
                                            ? "Aucune mesure de prévention"
                                            : "Mesure de prévention en place"}
                                        </span>
                                      </>
                                    )
                                  })()}
                                </div>
                                {isRiskIncomplete(risk) && (
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="flex items-center justify-center rounded-[50%] aspect-square flex-shrink-0"
                                      style={{ backgroundColor: getColor("red"), height: "24px" }}>
                                      <FontAwesomeIcon
                                        icon={faClipboardList}
                                        style={{
                                          fontSize: "12px",
                                          color: "white"
                                        }}
                                      />
                                    </div>
                                    <div
                                      className="text-xs px-2 py-1 rounded text-white w-fit"
                                      style={{ backgroundColor: getColor("red") }}>
                                      {t("À compléter")}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-auto gap-2">
                                <span className="text-sm text-gray-500">{t("Risque résiduel")}</span>
                                <div className="flex items-center gap-2">
                                  <Chip color={risk.valueEffectiveColor}>{risk.valueEffectiveLabel}</Chip>
                                  <RiskTypeChip
                                    isRps={risk.rps}
                                    showCount={false}
                                    color={risk.valueEffectiveColor || undefined}
                                    showLabel={false}
                                  />
                                </div>
                              </div>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
